import { computed, Injectable, linkedSignal, signal } from '@angular/core';
import type { PreviewRotation, PreviewZoom } from '../models';

export interface UiStateInput {
    previewUrl: string | null | undefined;
    fileName: string;
}

export type PanState = {
    readonly pointerId: number;
    readonly startX: number;
    readonly startY: number;
    readonly scrollLeft: number;
    readonly scrollTop: number;
};

@Injectable()
export class UiState {
    //#region State
    private readonly params = signal<UiStateInput>({
        previewUrl: undefined,
        fileName: '',
    });
    public readonly controlsVisible = linkedSignal({
        source: () => {
            const { previewUrl, fileName } = this.params();
            return { previewUrl, fileName };
        },
        computation: () => true,
    });
    public readonly zoom = linkedSignal({
        source: () => {
            const { previewUrl, fileName } = this.params();
            return { previewUrl, fileName };
        },
        computation: (): PreviewZoom => 'auto',
    });
    public readonly rotation = linkedSignal({
        source: () => {
            const { previewUrl, fileName } = this.params();
            return { previewUrl, fileName };
        },
        computation: (): PreviewRotation => 0,
    });
    public readonly handTool = linkedSignal({
        source: () => {
            const { previewUrl, fileName } = this.params();
            return { previewUrl, fileName };
        },
        computation: () => false,
    });
    public readonly rotationStyle = computed(() => `${this.rotation()}deg`);
    public readonly width = computed(() => {
        const zoom = this.zoom();

        if (typeof zoom === 'number') return `${zoom}%`;

        switch (zoom) {
            case 'page-fit':
            case 'page-actual':
                return 'auto';
            default:
                return '100%';
        }
    });
    public readonly maxWidth = computed(() => {
        const zoom = this.zoom();
        return zoom === 'page-actual' ||
            (typeof zoom === 'number' && zoom > 100)
            ? 'none'
            : '100%';
    });
    public readonly maxHeight = computed(() =>
        this.zoom() === 'page-fit' ? '100%' : 'none'
    );
    public panState: PanState | null = null;
    //#endregion

    //#region Public methods
    public setParams(params: UiStateInput): void {
        this.params.set(params);
    }

    public zoomOut(): void {
        this.zoom.set(this.stepZoomValue(this.zoom(), -25));
    }

    public zoomIn(): void {
        this.zoom.set(this.stepZoomValue(this.zoom(), 25));
    }

    public resetZoom(): void {
        this.zoom.set('auto');
    }

    public selectZoom(zoom: PreviewZoom): void {
        this.zoom.set(zoom ?? 'auto');
    }

    public rotateClockwise(): void {
        this.rotation.update(
            (rotation) => ((rotation + 90) % 360) as PreviewRotation
        );
    }

    public toggleHandTool(): void {
        this.handTool.update((active) => !active);
        if (!this.handTool()) {
            this.panState = null;
        }
    }

    public toggleControls(): void {
        this.controlsVisible.update((visible) => !visible);
    }

    //#endregion

    //#region Private methods
    private stepZoomValue(zoom: PreviewZoom, step: number): number {
        const numericZoom =
            typeof zoom === 'number'
                ? zoom
                : Number.parseInt(String(zoom ?? ''), 10);

        const baseZoom = Number.isNaN(numericZoom) ? 100 : numericZoom;
        return Math.min(Math.max(25, baseZoom + step), 400);
    }
    //#endregion
}
